import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function AIProductCard({ product }) {
  // Check if it's a combo or regular product
  const isCombo = product.itemType === 'combo';
  
  // Handle different data structures
  const name = product.name || product.title || 'Unknown Product';
  const images = product.images || [];
  const mainImage = images[0] || '';
  const fragranceType = product.fragranceType || (isCombo ? 'Gift Set' : 'Product');
  const productId = product.id;
  
  // Handle prices - combos have a single price, products have size-based pricing
  let lowestPrice = '0';
  let sizeLabel = '';
  
  if (isCombo) {
    // Combo has a single price
    lowestPrice = product.price || product.prices?.default || '0';
    sizeLabel = 'Gift Combo';
  } else {
    // Regular product with size-based pricing
    const prices = product.prices || {};
    const priceEntries = Object.entries(prices);
    
    if (priceEntries.length > 0) {
      lowestPrice = priceEntries[0][1];
      sizeLabel = priceEntries[0][0];
    }
  }

  // Determine link path
  const linkPath = isCombo ? `/combo-gifts` : `/product/${productId}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100"
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-50">
        {mainImage ? (
          <img
            src={mainImage}
            alt={name}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span className="text-gray-400 text-sm">No Image</span>
          </div>
        )}
        
        {/* Overlay Gradient (Subtle depth) */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Floating Badge */}
        <div className="absolute top-3 left-3">
          <span className="px-3 py-1 text-[10px] uppercase tracking-widest font-semibold bg-white/90 backdrop-blur-md border border-white/50 rounded-sm text-gray-800 shadow-sm">
            {fragranceType}
          </span>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5 flex flex-col gap-3">
        
        {/* Title & Price Row */}
        <div className="flex justify-between items-start gap-2">
          <h3
            className="text-lg leading-tight text-gray-900 truncate pr-2"
            style={{ fontFamily: "Losta Masta, serif" }}
          >
            {name}
          </h3>
          <div className="text-right shrink-0">
            <p className="font-serif text-lg text-[#D4AF37] font-medium">
              ₹{lowestPrice}
            </p>
          </div>
        </div>

        {/* Size Label */}
        <p className="text-xs uppercase tracking-widest text-gray-400 font-medium">
          {sizeLabel} {!isCombo && '/ Starting Price'}
        </p>

        {/* Divider */}
        <div className="h-px w-10 bg-[#D4AF37]/30 my-1 group-hover:w-full transition-all duration-500" />

        {/* Action Button */}
        <Link
          to={linkPath}
          className="relative overflow-hidden w-full text-center py-3 rounded-lg border border-[#D4AF37] text-[#B08927] font-medium text-sm tracking-wide transition-all duration-300 hover:text-white hover:border-[#D4AF37] group/btn"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {isCombo ? 'View Gift Set' : 'View Collection'}
            <svg 
              className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform duration-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </span>
          <div className="absolute inset-0 bg-[#D4AF37] transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-300 ease-out" />
        </Link>
      </div>
    </motion.div>
  );
}